/** @format */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { HashtagIcon, MagnifyingGlassIcon, UserGroupIcon, BellAlertIcon, PaperClipIcon, FaceSmileIcon, Bars3Icon, Squares2X2Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { collection, addDoc, onSnapshot, orderBy, query, updateDoc, doc, getDoc, limit, where, getDocs, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase/config';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import { useUsers } from '../hooks/useUsers';
import { renderTextWithLinks } from '../utils/formatUtils';
import CustomDropdown from '../components/CustomDropdown';
import AIChatButton from '../components/AIChatButton';
import UserProfileLink from '../components/UserProfileLink';

interface ForumThread {
  id?: string;
  title: string;
  topic: string;
  content: string;
  createdBy: string;
  createdByName: string;
  createdAt: any; // Can be Firestore timestamp or string
  updatedAt: any; // Can be Firestore timestamp or string
  comments: ForumComment[];
  images?: string[];
  profilePicture?: string;
  thumbnail?: string;
}

interface ForumComment {
  id?: string;
  authorId: string;
  authorName: string;
  content: string;
  createdAt: any; // Can be Firestore timestamp or string
  images?: string[];
}

interface UserProfile {
  avatar?: string;
  name?: string;
  role?: string;
}

interface TypingUser {
  userId: string;
  userName: string;
  timestamp: number;
}

// Emoji data
const emojis = [
  // Smileys & Emotions
  '😀', '😃', '😄', '😁', '😆', '😅', '😂', '🤣', '😊', '😇',
  '🙂', '🙃', '😉', '😌', '😍', '🥰', '😘', '😗', '😙', '😚',
  '😋', '😛', '😝', '😜', '🤪', '🤨', '🧐', '🤓', '😎', '🤩',
  '🥳', '😏', '😒', '😞', '😔', '😟', '😕', '🙁', '☹️', '😣',
  '😖', '😫', '😩', '🥺', '😢', '😭', '😤', '😠', '😡', '🤬',
  '🤯', '😳', '🥵', '🥶', '😱', '😨', '😰', '😥', '😓', '🤗',
  '🤔', '🤭', '🤫', '🤥', '😶', '😐', '😑', '😯', '😦', '😧',
  '😮', '😲', '🥱', '😴', '🤤', '😪', '😵', '🤐', '🥴', '🤢',
  '🤮', '🤧', '😷', '🤒', '🤕', '🤑', '🤠', '💩', '👻', '💀',
  '☠️', '👽', '👾', '🤖', '😺', '😸', '😹', '😻', '😼', '😽',
  '🙀', '😿', '😾', '🙈', '🙉', '🙊', '💌', '💘', '💝', '💖',
  '💗', '💓', '💞', '💕', '💟', '❣️', '💔', '❤️', '🧡', '💛',
  '💚', '💙', '💜', '🖤', '💯', '💢', '💥', '💫', '💦', '💨',
  '🕳️', '💬', '🗨️', '🗯️', '💭', '💤', '💋', '💯', '💢', '💥',
  
  // Gestures & Body Parts
  '👋', '🤚', '🖐️', '✋', '🖖', '👌', '🤌', '🤏', '✌️', '🤞',
  '🤟', '🤘', '🤙', '👈', '👉', '👆', '🖕', '👇', '☝️', '👍',
  '👎', '✊', '👊', '🤛', '🤜', '👏', '🙌', '👐', '🤲', '🤝',
  '🙏', '✍️', '💪', '🦾', '🦿', '🦵', '🦶', '👂', '🦻', '👃',
  '🧠', '🫀', '🫁', '🦷', '🦴', '👀', '👁️', '👅', '👄', '💋',
  
  // People & Fantasy
  '👶', '👧', '🧒', '👦', '👩', '🧑', '👨', '👵', '🧓', '👴',
  '👸', '🤴', '👳', '👳‍♀️', '👳‍♂️', '👲', '🧕', '🤵', '🤵‍♀️', '🤵‍♂️',
  '👰', '👰‍♀️', '👰‍♂️', '🤰', '🤱', '👼', '🎅', '🤶', '🧙', '🧙‍♀️',
  '🧙‍♂️', '🧚', '🧚‍♀️', '🧚‍♂️', '🧛', '🧛‍♀️', '🧛‍♂️', '🧜', '🧜‍♀️', '🧜‍♂️',
  
  // Animals & Nature
  '🐶', '🐱', '🐭', '🐹', '🐰', '🦊', '🐻', '🐼', '🐻‍❄️', '🐨',
  '🐯', '🦁', '🐮', '🐷', '🐸', '🐵', '🐔', '🐧', '🐦', '🐤',
  '🐣', '🦆', '🦅', '🦉', '🦇', '🐺', '🐗', '🐴', '🦄', '🐝',
  '🐛', '🦋', '🐌', '🐞', '🐜', '🦟', '🦗', '🕷️', '🕸️', '🦂',
  '🐢', '🐍', '🦎', '🦖', '🦕', '🐙', '🦑', '🦐', '🦞', '🦀',
  '🐡', '🐠', '🐟', '🐬', '🐳', '🐋', '🦈', '🐊', '🐅', '🐆',
  '🦓', '🦍', '🦧', '🐘', '🦛', '🦏', '🐪', '🐫', '🦙', '🦒',
  '🐃', '🐂', '🐄', '🐎', '🐖', '🐏', '🐑', '🐐', '🦌', '🐕',
  '🐩', '🦮', '🐕‍🦺', '🐈', '🐈‍⬛', '🐓', '🦃', '🦚', '🦜', '🦢',
  '🦩', '🕊️', '🐇', '🦝', '🦨', '🦡', '🦫', '🦦', '🦥', '🐁',
  '🐀', '🐿️', '🦔', '🐉', '🐲', '🌵', '🎄', '🌲', '🌳', '🌴',
  '🌱', '🌿', '☘️', '🍀', '🎍', '🪴', '🎋', '🍃', '🍂', '🍁',
  '🍄', '🐚', '🪨', '🌾', '💐', '🌷', '🌹', '🥀', '🌺', '🌻',
  '🌼', '🌸', '🌼', '🌻', '🌺', '🌹', '🌷', '🌼', '🌻', '🌺',
  
  // Food & Drink
  '🍎', '🍐', '🍊', '🍋', '🍌', '🍉', '🍇', '🍓', '🫐', '🍈',
  '🍒', '🍑', '🥭', '🍍', '🥥', '🥝', '🍅', '🍆', '🥑', '🥦',
  '🥬', '🥒', '🌶️', '🫑', '🌽', '🥕', '🫒', '🧄', '🧅', '🥔',
  '🍠', '🥐', '🥯', '🍞', '🥖', '🥨', '🧀', '🥚', '🍳', '🧈',
  '🥞', '🧇', '🥓', '🥩', '🍗', '🍖', '🦴', '🌭', '🍔', '🍟',
  '🍕', '🥪', '🥙', '🧆', '🌮', '🌯', '🫔', '🥗', '🥘', '🫕',
  '🥫', '🍝', '🍜', '🍲', '🍛', '🍣', '🍱', '🥟', '🦪', '🍤',
  '🍙', '🍚', '🍘', '🍥', '🥠', '🥮', '🍢', '🍡', '🍧', '🍨',
  '🍦', '🥧', '🧁', '🍰', '🎂', '🍮', '🍭', '🍬', '🍫', '🍿',
  '🍪', '🌰', '🥜', '🍯', '🥛', '🍼', '☕', '🫖', '🍵', '🧃',
  '🥤', '🧋', '🍶', '🍺', '🍷', '🥂', '🥃', '🍸', '🍹', '🧉',
  '🍾', '🥄', '🍴', '🍽️', '🥣', '🥡', '🥢', '🧂', '🥑', '🥦',
  
  // Activities & Sports
  '⚽', '🏀', '🏈', '⚾', '🥎', '🎾', '🏐', '🏉', '🥏', '🎱',
  '🪀', '🏓', '🏸', '🏒', '🏑', '🥍', '🏏', '🥅', '⛳', '🪁',
  '🏹', '🎣', '🤿', '🥊', '🥋', '🎽', '🛹', '🛷️', '⛸️', '🥌',
  '🎿', '⛷️', '🏂', '🪂', '🏋️', '🏋️‍♀️', '🏋️‍♂️', '🤼', '🤼‍♀️', '🤼‍♂️',
  '🤸', '🤸‍♀️', '🤸‍♂️', '⛹️', '⛹️‍♀️', '⛹️‍♂️', '🤺', '🤾', '🤾‍♀️', '🤾‍♂️',
  '🏊', '🏊‍♀️', '🏊‍♂️', '🚣', '🚣‍♀️', '🚣‍♂️', '🏄', '🏄‍♀️', '🏄‍♂️', '🚴',
  '🚴‍♀️', '🚴‍♂️', '🚵', '🚵‍♀️', '🚵‍♂️', '🏇', '🧘', '🧘‍♀️', '🧘‍♂️', '🏆',
  '🥇', '🥈', '🥉', '🏅', '🎖️', '🏆', '🎗️', '🎫', '🎟️', '🎪',
  '🤹', '🤹‍♀️', '🤹‍♂️', '🎭', '🩰', '🎨', '🎬', '🎤', '🎧', '🎼',
  '🎹', '🥁', '🪘', '🎷', '🎺', '🎸', '🪕', '🎻', '🎲', '♟️',
  '🎯', '🎳', '🎮', '🎰', '🧩', '🎨', '📱', '📲', '💻', '⌨️',
  
  // Travel & Places
  '🚗', '🚕', '🚙', '🚌', '🚎', '🏎️', '🚓', '🚑', '🚒', '🚐',
  '🚚', '🚛', '🚜', '🛴', '🛵', '🏍️', '🛺', '🚔', '🚍', '🚘',
  '🚖', '🚡', '🚠', '🚟', '🚃', '🚋', '🚞', '🚝', '🚄', '🚅',
  '🚈', '🚂', '🚆', '🚇', '🚊', '🚉', '✈️', '🛫', '🛬', '🛩️',
  '💺', '🛰️', '🚀', '🛸', '🚁', '🛶', '⛵', '🚤', '🛥️', '🛳️',
  '⛴️', '🚢', '⚓', '🚧', '⛽', '🚏', '🚦', '🚥', '🗺️', '🗿',
  '🗽', '🗼', '🏰', '🏯', '🏟️', '🎡', '🎢', '🎪', '⛲', '⛺',
  '🏖️', '🏝️', '🏔️', '🗻', '⛰️', '🌋', '🗾', '🏕️', '⛰️', '🏔️',
  
  // Weather & Nature
  '☀️', '🌤️', '⛅', '🌥️', '☁️', '🌦️', '🌧️', '⛈️', '🌩️', '🌨️',
  '☃️', '⛄', '🌬️', '💨', '🌪️', '🌫️', '🌊', '🌊', '💧', '💦',
  '☔', '☂️', '🌂', '🌂', '☂️', '☔', '💧', '💦', '🌊', '🌊',
  '🌫️', '🌪️', '💨', '🌬️', '⛄', '☃️', '🌨️', '🌩️', '⛈️', '🌧️',
  '🌦️', '☁️', '🌥️', '⛅', '🌤️', '☀️', '🌙', '🌔', '🌓', '🌍',
  '🌎', '🌏', '🌕', '🌖', '🌗', '🌘', '🌑', '🌒', '🌓', '🌔',
  
  // Objects & Symbols
  '💎', '🔶', '🔷', '🔸', '🔹', '🔺', '🔻', '💠', '🔘', '🔴',
  '🟠', '🟡', '🟢', '🔵', '🟣', '⚫', '⚪', '🟤', '🔺', '🔻',
  '💎', '🔶', '🔷', '🔸', '🔹', '🔺', '🔻', '💠', '🔘', '🔴',
  '🟠', '🟡', '🟢', '🔵', '🟣', '⚫', '⚪', '🟤', '🔺', '🔻',
  '💎', '🔶', '🔷', '🔸', '🔹', '🔺', '🔻', '💠', '🔘', '🔴',
  '🟠', '🟡', '🟢', '🔵', '🟣', '⚫', '⚪', '🟤', '🔺', '🔻'
];

const StudyForums: React.FC = () => {
  const { currentUser } = useAuth();
  const { fetchUsers } = useUsers();
  const [threads, setThreads] = useState<ForumThread[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [topic, setTopic] = useState('General');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [commentDrafts, setCommentDrafts] = useState<Record<string, string>>({});
  const [selectedTopic, setSelectedTopic] = useState<string>('all');
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [isCreatingThread, setIsCreatingThread] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const [userProfiles, setUserProfiles] = useState<Record<string, { avatar?: string; name?: string }>>({});
  const [threadFilter, setThreadFilter] = useState('');
  const [messageQuery, setMessageQuery] = useState('');
  const [threadComments, setThreadComments] = useState<ForumComment[]>([]);
  const [showServersMobile, setShowServersMobile] = useState(false);
  const [showChannelsMobile, setShowChannelsMobile] = useState(false);
  const [isRealTimeActive, setIsRealTimeActive] = useState(true);
  const [lastMessageTime, setLastMessageTime] = useState<Date>(new Date());
  const [newMessageCount, setNewMessageCount] = useState(0);
  const [previousCommentCount, setPreviousCommentCount] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [typingUsers, setTypingUsers] = useState<TypingUser[]>([]);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'connecting' | 'disconnected'>('connected');
  const [lastActivity, setLastActivity] = useState<Date>(new Date());
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [fullScreenImage, setFullScreenImage] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<{messageId: string, authorId: string, content: string} | null>(null);
  const [selectedMessages, setSelectedMessages] = useState<Set<string>>(new Set());
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  
  // Profile picture and thumbnail upload states
  const [threadProfilePicture, setThreadProfilePicture] = useState<File | null>(null);
  const [threadThumbnail, setThreadThumbnail] = useState<File | null>(null);
  const [uploadingThreadAssets, setUploadingThreadAssets] = useState(false);
  
  // Edit thread states
  const [showEditThread, setShowEditThread] = useState(false);
  const [editingTitle, setEditingTitle] = useState('');
  const [editingContent, setEditingContent] = useState('');
  const [editingTopic, setEditingTopic] = useState('');
  const [editingProfilePicture, setEditingProfilePicture] = useState<File | null>(null);
  const [editingThumbnail, setEditingThumbnail] = useState<File | null>(null);
  const [uploadingEditAssets, setUploadingEditAssets] = useState(false);
  
  // Members states
  const [showMembers, setShowMembers] = useState(false);
  const [forumMembers, setForumMembers] = useState<Array<{uid: string, name: string, avatar?: string, role: string, lastSeen?: Date}>>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const emojiPickerRef = useRef<HTMLDivElement>(null);
  const profilePictureRef = useRef<HTMLInputElement>(null);
  const thumbnailRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let cancelled = false;
    let unsubscribe: (() => void) | undefined;
    
    const run = async () => {
      setLoading(true);
      try {
        const colRef = collection(db, 'study_forums');
        const threadsQuery = (selectedTopic && selectedTopic !== 'all')
          ? query(colRef, where('topic', '==', selectedTopic), orderBy('createdAt', 'desc'), limit(50))
          : query(colRef, orderBy('createdAt', 'desc'), limit(50));
        
        // Use onSnapshot for real-time updates
        unsubscribe = onSnapshot(threadsQuery, (snap) => {
          if (cancelled) return;
          const list: ForumThread[] = [];
          snap.forEach((d) => list.push({ id: d.id, ...(d.data() as any) }));
          setThreads(list);
          setLoading(false);
        }, (error) => {
          console.error('Error listening to threads:', error);
          if (!cancelled) {
            setLoading(false);
          }
        });
      } catch (err) {
        console.error('Failed to load forums', err);
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    
    run();
    
    return () => { 
      cancelled = true;
      if (unsubscribe) unsubscribe();
    };
  }, [selectedTopic]);

  // Fetch user data for thread creators and message authors
  useEffect(() => {
    if (threads.length > 0) {
      const userIds = new Set<string>();
      
      // Add thread creators
      threads.forEach(thread => {
        if (thread.createdBy) {
          userIds.add(thread.createdBy);
        }
      });
      
      // Add message authors from selected thread
      if (selectedThreadId) {
        const selectedThread = threads.find(t => t.id === selectedThreadId);
        if (selectedThread?.comments) {
          selectedThread.comments.forEach(comment => {
            if (comment.authorId) {
              userIds.add(comment.authorId);
            }
          });
        }
      }
      
      if (userIds.size > 0) {
        fetchUsers(Array.from(userIds));
      }
    }
  }, [threads, selectedThreadId, fetchUsers]);

  const topics = useMemo(() => {
    const t = new Set<string>();
    threads.forEach((th) => t.add(th.topic));
    return ['all', ...Array.from(t).sort()];
  }, [threads]);

  const filtered = useMemo(() => {
    if (selectedTopic === 'all') return threads;
    return threads.filter((t) => t.topic === selectedTopic);
  }, [threads, selectedTopic]);

  const filteredBySearch = useMemo(() => {
    const q = threadFilter.trim().toLowerCase();
    if (!q) return filtered;
    return filtered.filter((t) => `${t.title} ${t.createdByName}`.toLowerCase().includes(q));
  }, [filtered, threadFilter]);

  const selectedThread = useMemo(() => {
    if (!selectedThreadId) return null;
    return filtered.find((t) => t.id === selectedThreadId) || null;
  }, [filtered, selectedThreadId]);

  useEffect(() => {
    if (!selectedThreadId && filtered.length > 0) {
      setSelectedThreadId(filtered[0].id || null);
    }
    if (selectedThreadId && filtered.every((t) => t.id !== selectedThreadId)) {
      setSelectedThreadId(filtered[0]?.id || null);
    }
  }, [filtered, selectedThreadId]);

  // Fetch avatars for participants in the selected thread
  useEffect(() => {
    if (!selectedThread) return;
    const participantIds = new Set<string>();
    if (selectedThread.createdBy) participantIds.add(selectedThread.createdBy);
    (threadComments || []).forEach((c) => participantIds.add(c.authorId));
    const toFetch = Array.from(participantIds).filter((uid) => !userProfiles[uid]);
    if (toFetch.length === 0) return;
    let cancelled = false;
    (async () => {
      const entries: Array<[string, { avatar?: string; name?: string }]> = [];
      await Promise.all(
        toFetch.map(async (uid) => {
          try {
            const snap = await getDoc(doc(db, 'users', uid));
            if (snap.exists()) {
              const data = snap.data() as any;
              entries.push([uid, { avatar: data.avatar, name: data.name }]);
            }
          } catch (err) {
            // ignore fetch error for avatar
          }
        })
      );
      if (!cancelled && entries.length) {
        setUserProfiles((prev) => {
          const next = { ...prev };
          for (const [uid, prof] of entries) next[uid] = prof;
          return next;
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedThread, threadComments, userProfiles]);

  // Subscribe to comments for the selected thread using a subcollection
  useEffect(() => {
    let cancelled = false;
    let unsubscribe: (() => void) | undefined;
    
    const loadComments = async () => {
      if (!selectedThreadId) {
        setThreadComments([]);
        setNewMessageCount(0);
        setPreviousCommentCount(0);
        return;
      }
      try {
        const commentsRef = collection(db, 'study_forums', selectedThreadId, 'comments');
        
        // Try to create the query with ordering, but fallback if there's an index issue
        let commentsQuery;
        try {
          commentsQuery = query(commentsRef, orderBy('createdAt', 'asc'), limit(100));
        } catch (orderError) {
          console.warn('Failed to create ordered query, falling back to unordered:', orderError);
          commentsQuery = query(commentsRef, limit(100));
        }
        
        // Use onSnapshot for real-time updates
        unsubscribe = onSnapshot(commentsQuery, (snap) => {
          if (cancelled) return;
          const list: ForumComment[] = [];
          snap.forEach((d) => list.push({ 
            id: d.id, // Include the document ID
            ...d.data() as ForumComment 
          }));
          
          // Comments are already ordered by Firestore, no need to sort again
          
          // Check for new messages
          if (list.length > previousCommentCount && previousCommentCount > 0) {
            const newMessages = list.length - previousCommentCount;
            setNewMessageCount(newMessages);
            
            // Play notification sound if enabled
            if (soundEnabled && newMessages > 0) {
              playNotificationSound();
            }
          }
          
          setThreadComments(list);
          setPreviousCommentCount(list.length);
          
          // Update last message time for real-time indicator
          if (list.length > 0) {
            const latestMessage = list[list.length - 1];
            setLastMessageTime(new Date(latestMessage.createdAt));
          }
          
          // Debug logging to help identify any ordering issues
          if (process.env.NODE_ENV === 'development') {
            console.log('Comments fetched:', list.map(c => ({
              id: c.id,
              author: c.authorName,
              time: c.createdAt,
              timestamp: new Date(c.createdAt).getTime()
            })));
          }
          
          // Auto-scroll to bottom for new messages
          setTimeout(() => {
            if (messagesEndRef.current) {
              messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
            }
          }, 100);
        }, (error) => {
          console.error('Error listening to comments:', error);
          if (!cancelled) {
            setThreadComments([]);
            // Try to fallback to thread comments if available
            if (selectedThread?.comments && selectedThread.comments.length > 0) {
              console.log('Falling back to thread comments due to error');
            }
          }
        });
      } catch (_) {
        if (!cancelled) setThreadComments([]);
      }
    };
    
    loadComments();
    
    return () => { 
      cancelled = true;
      if (unsubscribe) unsubscribe();
    };
  }, [selectedThreadId, previousCommentCount, soundEnabled]);

  // Subscribe to typing indicators for the selected thread
  useEffect(() => {
    let cancelled = false;
    let unsubscribe: (() => void) | undefined;
    
    if (!selectedThreadId) {
      setTypingUsers([]);
      return;
    }
    
    try {
      const typingRef = collection(db, 'study_forums', selectedThreadId, 'typing');
      unsubscribe = onSnapshot(typingRef, (snap) => {
        if (cancelled) return;
        const users: TypingUser[] = [];
        const now = Date.now();
        
        snap.forEach((doc) => {
          const data = doc.data() as TypingUser;
          // Only show users who typed in the last 5 seconds
          if (now - data.timestamp < 5000 && data.userId !== currentUser?.uid) {
            users.push(data);
          }
        });
        
        setTypingUsers(users);
      });
    } catch (error) {
      console.error('Error listening to typing indicators:', error);
    }
    
    return () => {
      cancelled = true;
      if (unsubscribe) unsubscribe();
      // Clear typing indicator when leaving thread
      if (currentUser) {
        updateTypingIndicator(false);
      }
    };
  }, [selectedThreadId, currentUser?.uid]);

  // Update typing indicator when user types
  const updateTypingIndicator = async (isTyping: boolean) => {
    if (!selectedThreadId || !currentUser) return;
    
    try {
      const typingRef = collection(db, 'study_forums', selectedThreadId, 'typing');
      const userTypingRef = doc(typingRef, currentUser.uid);
      
      if (isTyping) {
        await updateDoc(userTypingRef, {
          userId: currentUser.uid,
          userName: currentUser.name || currentUser.email || 'Student',
          timestamp: Date.now()
        });
      } else {
        // Remove typing indicator when user stops typing
        await updateDoc(userTypingRef, {
          userId: currentUser.uid,
          userName: currentUser.name || currentUser.email || 'Student',
          timestamp: 0
        });
      }
    } catch (error) {
      console.error('Error updating typing indicator:', error);
    }
  };

  // Handle typing in comment input
  const handleCommentInputChange = (threadId: string, value: string) => {
    setCommentDrafts((prev) => ({ ...prev, [threadId]: value }));
    
    // Update typing indicator
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    updateTypingIndicator(true);
    
    // Clear typing indicator after 2 seconds of no typing
    typingTimeoutRef.current = setTimeout(() => {
      updateTypingIndicator(false);
    }, 2000);
  };

  // Helper function to safely convert Firestore timestamps to Date objects
  const safeTimestampToDate = (timestamp: any): Date => {
    if (!timestamp) return new Date();
    if (typeof timestamp === 'string') return new Date(timestamp);
    if (timestamp.toDate && typeof timestamp.toDate === 'function') return timestamp.toDate();
    return new Date(timestamp);
  };

  // Play notification sound for new messages
  const playNotificationSound = () => {
    try {
      const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYIG2m98OScTgwOUarm7blmGgU7k9n1unEiBC13yO/eizEIHWq+8+OWT');
      audio.volume = 0.3;
      audio.play().catch(() => {
        // Ignore audio play errors
      });
    } catch (error) {
      // Ignore audio errors
    }
  };

  // Clear new message count when user scrolls to bottom
  const handleScrollToBottom = () => {
    setNewMessageCount(0);
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const expandedMessages = useMemo(() => {
    if (!selectedThread) return [] as Array<{ id?: string; authorId: string; authorName: string; content: string; createdAt: string; images?: string[] }>;
    
    // Create the thread message
    const threadMessage = {
      id: 'thread',
      authorId: selectedThread.createdBy,
      authorName: selectedThread.createdByName,
      content: selectedThread.content,
      createdAt: selectedThread.createdAt,
      images: selectedThread.images,
    };
    
    // Get comments (either from real-time updates or fallback to thread data)
    let comments = (threadComments && threadComments.length > 0 ? threadComments : (selectedThread.comments || [])).map((c) => ({
      id: c.id || `comment_${c.createdAt}_${c.authorId}`,
      authorId: c.authorId,
      authorName: c.authorName,
      content: c.content,
      createdAt: c.createdAt,
      images: c.images,
    }));
    
    // Combine all messages
    const allMessages = [threadMessage, ...comments];
    
    // Sort by creation time - OLDEST FIRST (ascending order)
    const sortedMessages = allMessages.sort((a, b) => {
      // Parse timestamps and ensure they're valid
      const timeA = safeTimestampToDate(a.createdAt).getTime();
      const timeB = safeTimestampToDate(b.createdAt).getTime();
      
      // Check if timestamps are valid
      if (isNaN(timeA) || isNaN(timeB)) {
        console.warn('Invalid timestamp detected:', { a: a.createdAt, b: b.createdAt });
        return 0; // Keep original order if timestamps are invalid
      }
      
      // Ascending order: oldest first, newest last (like WhatsApp)
      return timeA - timeB;
    });
    
    // Debug logging to help identify any ordering issues
    if (process.env.NODE_ENV === 'development') {
      console.log('Message ordering debug:', sortedMessages.map(m => ({
        id: m.id,
        author: m.authorName,
        time: m.createdAt,
        timestamp: safeTimestampToDate(m.createdAt).getTime(),
        sortOrder: 'oldest first, newest last'
      })));
      
      // Verify the order is correct
      if (sortedMessages.length > 1) {
        const firstTime = safeTimestampToDate(sortedMessages[0].createdAt).getTime();
        const lastTime = safeTimestampToDate(sortedMessages[sortedMessages.length - 1].createdAt).getTime();
        console.log('Order verification:', {
          firstMessage: sortedMessages[0].authorName,
          firstTime: sortedMessages[0].createdAt,
          firstTimestamp: firstTime,
          lastMessage: sortedMessages[sortedMessages.length - 1].authorName,
          lastTime: sortedMessages[sortedMessages.length - 1].createdAt,
          lastTimestamp: lastTime,
          isCorrectOrder: firstTime <= lastTime
        });
      }
    }
    
    return sortedMessages;
  }, [selectedThread, threadComments]);

  const visibleMessages = useMemo(() => {
    const q = messageQuery.trim().toLowerCase();
    if (!q) return expandedMessages;
    
    // Filter messages while maintaining the chronological order
    const filtered = (expandedMessages as any[]).filter((m) => 
      `${m.authorName} ${m.content}`.toLowerCase().includes(q)
    );
    
    // Ensure filtered results maintain the same order as expandedMessages
    // This is important because expandedMessages is already sorted chronologically
    // The filter operation preserves the original order, so no additional sorting is needed
    
    // Debug logging for search results
    if (process.env.NODE_ENV === 'development' && q) {
      console.log('Search results:', {
        query: q,
        totalMessages: expandedMessages.length,
        filteredCount: filtered.length,
        firstResult: filtered[0]?.authorName,
        lastResult: filtered[filtered.length - 1]?.authorName,
        order: filtered.map((m, idx) => ({ idx, author: m.authorName, time: m.createdAt })),
        originalOrder: expandedMessages.map((m, idx) => ({ idx, author: m.authorName, time: m.createdAt }))
      });
    }
    
    return filtered;
  }, [expandedMessages, messageQuery]);

  const createThread = async () => {
    if (!currentUser) return;
    
    setUploadingThreadAssets(true);
    
    try {
      // Upload profile picture and thumbnail if selected
      let profilePictureUrl: string | null = null;
      let thumbnailUrl: string | null = null;
      
      if (threadProfilePicture) {
        profilePictureUrl = await uploadThreadProfilePicture();
      }
      
      if (threadThumbnail) {
        thumbnailUrl = await uploadThreadThumbnail();
      }
      
      // Create thread with uploaded assets
      await addDoc(collection(db, 'study_forums'), {
        title: title.trim(),
        topic,
        content: content.trim(),
        createdBy: currentUser.uid,
        createdByName: currentUser.name || currentUser.email || 'Student',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        comments: [],
        profilePicture: profilePictureUrl,
        thumbnail: thumbnailUrl,
      });
      
      // Reset form
      setTitle('');
      setContent('');
      setThreadProfilePicture(null);
      setThreadThumbnail(null);
      setIsCreatingThread(false);
      setSelectedTopic(topic);
      
    } catch (error) {
      console.error('Error creating thread:', error);
    } finally {
      setUploadingThreadAssets(false);
    }
  };

  // Initialize edit thread form
  const initializeEditThread = () => {
    if (!selectedThread) return;
    setEditingTitle(selectedThread.title);
    setEditingContent(selectedThread.content);
    setEditingTopic(selectedThread.topic);
    setEditingProfilePicture(null);
    setEditingThumbnail(null);
    setShowEditThread(true);
  };

  // Update thread
  const updateThread = async () => {
    if (!currentUser || !selectedThread || !selectedThread.id) return;
    
    setUploadingEditAssets(true);
    
    try {
      // Upload new profile picture and thumbnail if selected
      let profilePictureUrl: string | null = selectedThread.profilePicture || null;
      let thumbnailUrl: string | null = selectedThread.thumbnail || null;
      
      if (editingProfilePicture) {
        profilePictureUrl = await uploadThreadProfilePicture();
      }
      
      if (editingThumbnail) {
        thumbnailUrl = await uploadThreadThumbnail();
      }
      
      // Update thread document
      const threadRef = doc(db, 'study_forums', selectedThread.id);
      await updateDoc(threadRef, {
        title: editingTitle.trim(),
        content: editingContent.trim(),
        topic: editingTopic,
        profilePicture: profilePictureUrl,
        thumbnail: thumbnailUrl,
        updatedAt: serverTimestamp(),
      });
      
      // Close edit modal
      setShowEditThread(false);
      
    } catch (error) {
      console.error('Error updating thread:', error);
    } finally {
      setUploadingEditAssets(false);
    }
  };

  // Handle edit profile picture selection
  const handleEditProfilePictureSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      setEditingProfilePicture(file);
    }
  };

  // Handle edit thumbnail selection
  const handleEditThumbnailSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      setEditingThumbnail(file);
    }
  };

  // Remove edit profile picture
  const removeEditProfilePicture = () => {
    setEditingProfilePicture(null);
  };

  // Remove edit thumbnail
  const removeEditThumbnail = () => {
    setEditingThumbnail(null);
  };

  const addComment = async (threadId: string) => {
    if (!currentUser) return;
    const text = (commentDrafts[threadId] || '').trim();
    if (!text && selectedImages.length === 0) return;
    
    // Clear typing indicator when sending message
    updateTypingIndicator(false);
    
    // Upload images first if any are selected
    let imageUrls: string[] = [];
    if (selectedImages.length > 0) {
      imageUrls = await uploadImages();
    }
    
    const threadRef = doc(db, 'study_forums', threadId);
    const commentsRef = collection(db, 'study_forums', threadId, 'comments');
    
    // Create comment data object, only including images if they exist
    const commentData: any = {
      authorId: currentUser.uid,
      authorName: currentUser.name || currentUser.email || 'Student',
      content: text,
      createdAt: serverTimestamp(),
    };
    
    // Only add images field if there are actual images
    if (imageUrls.length > 0) {
      commentData.images = imageUrls;
    }
    
    // Write comment to subcollection and update thread metadata only
    await addDoc(commentsRef, commentData);
    await updateDoc(threadRef, { updatedAt: serverTimestamp() });
    setCommentDrafts((prev) => ({ ...prev, [threadId]: '' }));
    
    // Clear selected images after successful upload
    setSelectedImages([]);
    
    // Auto-scroll to bottom after a short delay to ensure the new message is rendered
    setTimeout(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100); // Increased delay to ensure proper rendering
  };

  // Cleanup typing indicators and timeouts on unmount
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      if (currentUser) {
        updateTypingIndicator(false);
      }
    };
  }, [currentUser]);

  // Monitor connection status
  useEffect(() => {
    const updateConnectionStatus = (status: 'connected' | 'connecting' | 'disconnected') => {
      setConnectionStatus(status);
      setIsRealTimeActive(status === 'connected');
    };

    // Set initial connection status
    updateConnectionStatus('connected');
    
    // Simple connection monitoring through successful operations
    const checkConnection = () => {
      if (selectedThreadId) {
        // Try to access a document to check connection
        getDoc(doc(db, 'study_forums', selectedThreadId))
          .then(() => updateConnectionStatus('connected'))
          .catch(() => updateConnectionStatus('disconnected'));
      }
    };

    // Check connection every 30 seconds
    const interval = setInterval(checkConnection, 30000);
    
    return () => clearInterval(interval);
  }, [selectedThreadId]);

  // Update last activity timestamp
  useEffect(() => {
    const interval = setInterval(() => {
      setLastActivity(new Date());
    }, 30000); // Update every 30 seconds

    return () => clearInterval(interval);
  }, []);

  // Handle image selection
  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    
    if (imageFiles.length > 0) {
      setSelectedImages(prev => [...prev, ...imageFiles]);
      setShowImagePicker(false);
    }
  };

  // Remove selected image
  const removeSelectedImage = (index: number) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
  };

  // Upload images to Firebase Storage
  const uploadImages = async (): Promise<string[]> => {
    if (selectedImages.length === 0) return [];
    
    setUploadingImages(true);
    const uploadPromises = selectedImages.map(async (image) => {
      const imageRef = ref(storage, `forum_images/${Date.now()}_${image.name}`);
      await uploadBytes(imageRef, image);
      return getDownloadURL(imageRef);
    });
    
    try {
      const urls = await Promise.all(uploadPromises);
      setSelectedImages([]);
      return urls;
    } catch (error) {
      console.error('Error uploading images:', error);
      return [];
    } finally {
      setUploadingImages(false);
    }
  };

  // Upload thread profile picture
  const uploadThreadProfilePicture = async (): Promise<string | null> => {
    if (!threadProfilePicture) return null;
    
    try {
      const imageRef = ref(storage, `forum_profiles/${Date.now()}_${threadProfilePicture.name}`);
      await uploadBytes(imageRef, threadProfilePicture);
      return getDownloadURL(imageRef);
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      return null;
    }
  };

  // Upload thread thumbnail
  const uploadThreadThumbnail = async (): Promise<string | null> => {
    if (!threadThumbnail) return null;
    
    try {
      const imageRef = ref(storage, `forum_thumbnails/${Date.now()}_${threadThumbnail.name}`);
      await uploadBytes(imageRef, threadThumbnail);
      return getDownloadURL(imageRef);
    } catch (error) {
      console.error('Error uploading thumbnail:', error);
      return null;
    }
  };

  // Handle profile picture selection
  const handleProfilePictureSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      setThreadProfilePicture(file);
    }
  };

  // Handle thumbnail selection
  const handleThumbnailSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      setThreadThumbnail(file);
    }
  };

  // Remove profile picture
  const removeProfilePicture = () => {
    setThreadProfilePicture(null);
    if (profilePictureRef.current) {
      profilePictureRef.current.value = '';
    }
  };

  // Remove thumbnail
  const removeThumbnail = () => {
    setThreadThumbnail(null);
    if (thumbnailRef.current) {
      thumbnailRef.current.value = '';
    }
  };

  // Fetch forum members
  const fetchForumMembers = async () => {
    if (!selectedThread) return;
    
    setLoadingMembers(true);
    try {
      const members: Array<{uid: string, name: string, avatar?: string, role: string, lastSeen?: Date}> = [];
      
      // Add thread creator
      if (selectedThread.createdBy) {
        const creatorDoc = await getDoc(doc(db, 'users', selectedThread.createdBy));
        if (creatorDoc.exists()) {
          const creatorData = creatorDoc.data() as any;
          members.push({
            uid: selectedThread.createdBy,
            name: creatorData.name || creatorData.email || 'Unknown User',
            avatar: creatorData.avatar,
            role: 'Creator',
            lastSeen: new Date()
          });
        }
      }
      
      // Add comment authors
      const uniqueAuthors = new Set<string>();
      if (selectedThread.comments) {
        selectedThread.comments.forEach(comment => {
          if (comment.authorId && !uniqueAuthors.has(comment.authorId)) {
            uniqueAuthors.add(comment.authorId);
          }
        });
      }
      
      // Fetch author details
      for (const authorId of Array.from(uniqueAuthors)) {
        if (authorId === selectedThread.createdBy) continue; // Skip creator as already added
        
        try {
          const authorDoc = await getDoc(doc(db, 'users', authorId));
          if (authorDoc.exists()) {
            const authorData = authorDoc.data() as any;
            members.push({
              uid: authorId,
              name: authorData.name || authorData.email || 'Unknown User',
              avatar: authorData.avatar,
              role: 'Member',
              lastSeen: new Date()
            });
          }
        } catch (error) {
          console.error('Error fetching author:', error);
        }
      }
      
      setForumMembers(members);
    } catch (error) {
      console.error('Error fetching forum members:', error);
    } finally {
      setLoadingMembers(false);
    }
  };

  // Add emoji to comment
  const addEmoji = (emoji: string) => {
    if (!selectedThreadId) return;
    
    const currentDraft = commentDrafts[selectedThreadId] || '';
    setCommentDrafts(prev => ({
      ...prev,
      [selectedThreadId]: currentDraft + emoji
    }));
    
    setShowEmojiPicker(false);
  };

  // Close emoji picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target as Node)) {
        setShowEmojiPicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close full-screen image viewer
  const closeFullScreenImage = () => {
    setFullScreenImage(null);
  };

  // Handle image click to open full-screen view
  const openFullScreenImage = (imageUrl: string) => {
    setFullScreenImage(imageUrl);
  };

  // Get user profile picture
  const getUserProfilePicture = (userId: string): string | undefined => {
    if (userId === currentUser?.uid) {
      return currentUser?.avatar;
    }
    return userProfiles[userId]?.avatar;
  };

  // Get user display name
  const getUserDisplayName = (userId: string): string => {
    if (userId === currentUser?.uid) {
      return currentUser?.name || currentUser?.email || 'You';
    }
    return userProfiles[userId]?.name || 'Student';
  };

  // Generate initials for avatar fallback
  const getInitials = (name: string): string => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Get avatar background color based on user ID
  const getAvatarColor = (userId: string): string => {
    const colors = [
      'bg-[#5865f2]', 'bg-[#3ba55d]', 'bg-[#faa61a]', 'bg-[#f04747]',
      'bg-[#747f8d]', 'bg-[#9b59b6]', 'bg-[#e67e22]', 'bg-[#1abc9c]'
    ];
    const index = userId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length;
    return colors[index];
  };

  // Check if user is admin
  const isAdmin = currentUser?.role === 'admin' || currentUser?.email === 'admin@profstudy.com';

  // Check if user can delete a message
  const canDeleteMessage = (messageAuthorId: string) => {
    return currentUser && (currentUser.uid === messageAuthorId || isAdmin);
  };

  // Check if user can select a message (only own messages, or admin/creator can select any)
  const canSelectMessage = (messageAuthorId: string) => {
    if (!currentUser) return false;
    
    // User can always select their own messages
    if (currentUser.uid === messageAuthorId) return true;
    
    // Admin can select any message
    if (isAdmin) return true;
    
    // Forum creator can select any message in their thread
    if (selectedThread && selectedThread.createdBy === currentUser.uid) return true;
    
    return false;
  };

  // Delete a message
  const deleteMessage = async (messageId: string, isThreadMessage: boolean = false) => {
    if (!currentUser || !selectedThreadId) return;
    
    try {
      if (isThreadMessage) {
        // Delete the main thread message (only admin can do this)
        if (!isAdmin) return;
        // Completely remove the thread message by deleting the document
        await deleteDoc(doc(db, 'study_forums', selectedThreadId));
        // Navigate back since the thread is now deleted
        window.history.back();
        return;
      } else {
        // Delete a comment - we need to find the comment document first
        if (!messageId || messageId === '') {
          console.error('No message ID provided for comment deletion');
          return;
        }
        
        // Find the comment in the threadComments array to get the document ID
        const commentToDelete = threadComments.find(c => c.id === messageId);
        if (!commentToDelete) {
          console.error('Comment not found for deletion');
          return;
        }
        
        // Completely remove the comment document
        const commentRef = doc(db, 'study_forums', selectedThreadId, 'comments', messageId);
        await deleteDoc(commentRef);
      }
      
      setShowDeleteConfirm(null);
      
      // Force a refresh of the messages to ensure proper ordering
      // This is important because deletion might affect the message order
      if (selectedThreadId) {
        // The onSnapshot listener will automatically update the messages
        // and maintain proper chronological order
      }
    } catch (error) {
      console.error('Error deleting message:', error);
    }
  };

  // Multiple message selection functions
  const toggleMessageSelection = (messageId: string) => {
    // Find the message to check permissions
    const message = (expandedMessages as any[]).find(m => m.id === messageId);
    if (!message) return;
    
    // Check if user can select this message
    if (!canSelectMessage(message.authorId)) {
      console.warn('User not authorized to select this message');
      return;
    }
    
    setSelectedMessages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(messageId)) {
        newSet.delete(messageId);
      } else {
        newSet.add(messageId);
      }
      return newSet;
    });
  };

  const selectAllMessages = () => {
    if (!expandedMessages) return;
    // Only select messages that the user has permission to select
    const selectableMessageIds = (expandedMessages as any[])
      .map(m => m.id || '')
      .filter(id => id !== '')
      .filter(id => {
        const message = (expandedMessages as any[]).find(m => m.id === id);
        return message && canSelectMessage(message.authorId);
      });
    setSelectedMessages(new Set(selectableMessageIds));
  };

  const clearMessageSelection = () => {
    setSelectedMessages(new Set());
  };

  const deleteSelectedMessages = async () => {
    if (!currentUser || !selectedThreadId || selectedMessages.size === 0) return;
    
    try {
      const deletePromises = Array.from(selectedMessages).map(async (messageId) => {
        if (messageId === 'thread') {
          // Thread message can only be deleted by admin
          if (!isAdmin) return;
          await deleteDoc(doc(db, 'study_forums', selectedThreadId));
          window.history.back();
          return;
        } else {
          // Delete comment
          const commentRef = doc(db, 'study_forums', selectedThreadId, 'comments', messageId);
          await deleteDoc(commentRef);
        }
      });
      
      await Promise.all(deletePromises);
      setSelectedMessages(new Set());
      setShowBulkDeleteConfirm(false);
      setIsSelectionMode(false);
    } catch (error) {
      console.error('Error deleting selected messages:', error);
    }
  };

  const toggleSelectionMode = () => {
    setIsSelectionMode(!isSelectionMode);
    if (isSelectionMode) {
      clearMessageSelection();
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex flex-col bg-[#1e1f22] overflow-hidden px-2 md:px-4">
      <div className="mb-4 flex items-center justify-between pt-3">
        <div className="flex items-center gap-4">
          <button
            onClick={() => window.history.back()}
            className="p-2 rounded-lg bg-[#2b2d31] text-gray-200 hover:bg-[#313338] transition-colors"
            title="Go back"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-100">Study Forums</h1>
            <p className="text-gray-400 text-sm">Discord-like collaborative forums</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Selection mode toggle */}
          <button
            onClick={toggleSelectionMode}
            className={`p-2 rounded-lg transition-colors ${
              isSelectionMode 
                ? 'bg-[#5865f2] text-white' 
                : 'bg-[#2b2d31] text-gray-200 hover:bg-[#313338]'
            }`}
            title={isSelectionMode ? 'Exit selection mode' : 'Enter selection mode'}
          >
            {isSelectionMode ? (
              // Checkmark icon when in selection mode
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              // Checkbox icon when not in selection mode
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            )}
          </button>
          
          {/* Bulk actions when in selection mode */}
          {isSelectionMode && selectedMessages.size > 0 && (
            <>
              <button
                onClick={selectAllMessages}
                className="px-3 py-2 bg-[#313338] hover:bg-[#404040] text-gray-200 rounded text-sm"
              >
                Select All
              </button>
              <button
                onClick={() => setShowBulkDeleteConfirm(true)}
                className="px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded text-sm"
              >
                Delete ({selectedMessages.size})
              </button>
              <button
                onClick={clearMessageSelection}
                className="px-3 py-2 bg-[#313338] hover:bg-[#404040] text-gray-200 rounded text-sm"
              >
                Clear
              </button>
            </>
          )}
          
          <div className="md:hidden flex items-center gap-2">
            <button onClick={() => setShowServersMobile(true)} className="p-2 rounded bg-[#2b2d31] text-gray-200">
              <Squares2X2Icon className="w-5 h-5" />
            </button>
            <button onClick={() => setShowChannelsMobile(true)} className="p-2 rounded bg-[#2b2d31] text-gray-200">
              <Bars3Icon className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      <div className="flex flex-1 min-h-0">
        {/* Servers/Topics rail */}
        <div className="hidden md:flex w-20 bg-[#1e1f22] text-gray-200 flex-col items-center py-3 space-y-3">
          {topics.map((t) => {
            const isActive = selectedTopic === t;
            const label = t === 'all' ? 'A' : t.charAt(0).toUpperCase();
            return (
              <button
                key={t}
                onClick={() => setSelectedTopic(t)}
                className={`w-12 h-12 rounded-full flex items-center justify-center text-sm font-semibold transition-transform ${isActive ? 'bg-[#5865f2] text-white scale-105' : 'bg-[#313338] text-gray-300 hover:scale-105'}`}
                title={t}
              >
                {label}
              </button>
            );
          })}
        </div>

        {/* Channels column */}
        <div className="hidden md:flex w-72 bg-[#2b2d31] text-gray-200 flex-col">
          <div className="px-3 py-3 border-b border-[#1e1f22] flex items-center justify-between">
            <div className="text-sm font-semibold truncate">{selectedTopic === 'all' ? 'All Topics' : selectedTopic}</div>
            <button
              onClick={() => setIsCreatingThread((v) => !v)}
              className="text-xs bg-[#5865f2] hover:bg-[#4854d4] text-white px-2 py-1 rounded"
              disabled={!currentUser}
            >
              New
            </button>
          </div>

          <div className="px-3 py-2">
            <div className="relative">
              <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-2 top-2.5" />
              <input
                value={threadFilter}
                onChange={(e) => setThreadFilter(e.target.value)}
                placeholder="Search threads"
                className="w-full pl-7 pr-2 py-2 rounded bg-[#1e1f22] text-gray-100 text-sm placeholder-gray-400"
              />
            </div>
          </div>

          {isCreatingThread && (
            <div className="p-3 border-b border-[#1e1f22] space-y-2">
              <input
                type="text"
                placeholder="Thread title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-2 py-2 rounded bg-[#1e1f22] text-gray-100 text-sm placeholder-gray-400"
              />
              
              {/* Profile picture and thumbnail uploads */}
              <div className="space-y-3">
                {/* Profile Picture Upload */}
                <div className="p-3 bg-[#2b2d31] rounded">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="flex-shrink-0">
                      {threadProfilePicture ? (
                        <img 
                          src={URL.createObjectURL(threadProfilePicture)} 
                          alt="Thread profile" 
                          className="w-12 h-12 rounded-full object-cover border border-[#1e1f22]"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-[#404249] flex items-center justify-center border-2 border-dashed border-[#5865f2]">
                          <svg className="w-6 h-6 text-[#5865f2]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-100">Thread Profile Picture</div>
                      <div className="text-xs text-gray-400">Upload a profile picture for this thread</div>
                    </div>
                    <div className="flex gap-2">
                      <input
                        ref={profilePictureRef}
                        type="file"
                        accept="image/*"
                        onChange={handleProfilePictureSelect}
                        className="hidden"
                      />
                      <button
                        onClick={() => profilePictureRef.current?.click()}
                        className="px-3 py-1 bg-[#5865f2] hover:bg-[#4752c4] text-white text-xs rounded transition-colors"
                      >
                        {threadProfilePicture ? 'Change' : 'Upload'}
                      </button>
                      {threadProfilePicture && (
                        <button
                          onClick={removeProfilePicture}
                          className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded transition-colors"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Thumbnail Upload */}
                <div className="p-3 bg-[#2b2d31] rounded">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="flex-shrink-0">
                      {threadThumbnail ? (
                        <img 
                          src={URL.createObjectURL(threadThumbnail)} 
                          alt="Thread thumbnail" 
                          className="w-16 h-12 rounded object-cover border border-[#1e1f22]"
                        />
                      ) : (
                        <div className="w-16 h-12 rounded bg-[#404249] flex items-center justify-center border-2 border-dashed border-[#5865f2]">
                          <svg className="w-6 h-6 text-[#5865f2]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-100">Thread Thumbnail</div>
                      <div className="text-xs text-gray-400">Upload a thumbnail image for this thread</div>
                    </div>
                    <div className="flex gap-2">
                      <input
                        ref={thumbnailRef}
                        type="file"
                        accept="image/*"
                        onChange={handleThumbnailSelect}
                        className="hidden"
                      />
                      <button
                        onClick={() => thumbnailRef.current?.click()}
                        className="px-3 py-1 bg-[#5865f2] hover:bg-[#4752c4] text-white text-xs rounded transition-colors"
                      >
                        {threadThumbnail ? 'Change' : 'Upload'}
                      </button>
                      {threadThumbnail && (
                        <button
                          onClick={removeThumbnail}
                          className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded transition-colors"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <textarea
                placeholder="What do you want to discuss?"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                className="w-full px-2 py-2 rounded bg-[#1e1f22] text-gray-100 text-sm min-h-[70px] placeholder-gray-400"
              />
              <div className="flex items-center gap-2">
                {/* First topic select - desktop */}
                <CustomDropdown
                  value={topic}
                  onChange={setTopic}
                  options={['General', 'ICAG', 'CITG', 'Accounting', 'Taxation', 'Audit', 'Exam Prep'].map((t) => ({
                    value: t,
                    label: t
                  }))}
                  placeholder="Select Topic"
                  className="flex-1 px-2 py-2 rounded bg-[#1e1f22] text-gray-100 text-sm"
                />
                <button
                  onClick={createThread}
                  disabled={!currentUser || !title.trim() || !content.trim() || uploadingThreadAssets}
                  className="px-3 py-2 bg-[#5865f2] hover:bg-[#4854d4] text-white rounded text-sm disabled:opacity-50 flex items-center gap-2"
                >
                  {uploadingThreadAssets ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Uploading...
                    </>
                  ) : (
                    'Post'
                  )}
                </button>
              </div>
              {!currentUser && (
                <div className="text-[11px] text-gray-400">Sign in to create a thread.</div>
              )}
            </div>
          )}

          <div className="px-3 py-2 text-[11px] uppercase tracking-wide text-gray-400">Threads</div>
          <div className="flex-1 overflow-y-auto">
            {loading && <div className="px-3 py-2 text-gray-400 text-sm">Loading...</div>}
            {filteredBySearch.map((th) => {
              const isActive = selectedThreadId === th.id;
              const messagesCount = (th.comments?.length || 0) + 1;
              return (
                <button
                  key={th.id}
                  onClick={() => setSelectedThreadId(th.id || null)}
                  className={`w-full text-left px-3 py-2 hover:bg-[#404249] ${isActive ? 'bg-[#404249]' : ''}`}
                >
                  <div className="flex items-center gap-3">
                    {/* Thread Profile Picture - Only show when exists */}
                    {th.profilePicture && (
                      <div className="flex-shrink-0">
                        <img 
                          src={th.profilePicture} 
                          alt="Thread profile" 
                          className="w-10 h-10 rounded-full object-cover border border-[#1e1f22]"
                        />
                      </div>
                    )}
                    
                    {/* Thread Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-sm text-gray-100 truncate"># {th.title}</div>
                        <span className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1 rounded-full bg-[#1e1f22] text-gray-300 text-[11px]">{messagesCount}</span>
                      </div>
                      <div className="text-[11px] text-gray-400 truncate">
                        {th.createdBy ? (
                          <UserProfileLink 
                            userId={th.createdBy}
                            className="hover:text-[#5865f2] transition-colors"
                          />
                        ) : (
                          th.createdByName
                        )} • {new Date(th.updatedAt || th.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
            {filteredBySearch.length === 0 && (
              <div className="px-3 py-2 text-gray-400 text-sm">No threads yet.</div>
            )}
          </div>
        </div>

        {/* Chat area */}
        <div className="flex-1 bg-[#313338] text-gray-100 flex flex-col">
          <div className="px-4 py-3 border-b border-[#1e1f22] flex items-center gap-3">
            <HashtagIcon className="w-5 h-5 text-gray-300" />
            
            {/* Thread Profile Picture */}
            {selectedThread?.profilePicture && (
              <div className="flex-shrink-0">
                <img 
                  src={selectedThread.profilePicture} 
                  alt="Thread profile" 
                  className="w-8 h-8 rounded-full object-cover border border-[#1e1f22]"
                />
              </div>
            )}
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3">
                <div className="text-lg font-semibold truncate">{selectedThread ? selectedThread.title : 'Select a thread'}</div>
                
                {/* Edit Button for Thread Creator */}
                {selectedThread && currentUser && selectedThread.createdBy === currentUser.uid && (
                  <button
                    onClick={initializeEditThread}
                    className="p-1.5 bg-[#5865f2] hover:bg-[#4752c4] text-white rounded transition-colors flex-shrink-0"
                    title="Edit thread"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                )}
              </div>
              
              {/* Thread Thumbnail Preview */}
              {selectedThread?.thumbnail && (
                <div className="mt-1">
                  <img 
                    src={selectedThread.thumbnail} 
                    alt="Thread thumbnail" 
                    className="w-16 h-8 rounded object-cover border border-[#1e1f22] cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => openFullScreenImage(selectedThread.thumbnail!)}
                    title="Click to view full size"
                  />
                </div>
              )}
            </div>
            <div className="ml-auto hidden md:flex items-center gap-2">
              {/* Connection status indicator */}
              <div className="flex items-center gap-2 text-xs">
                <div className={`w-2 h-2 rounded-full ${
                  connectionStatus === 'connected' ? 'bg-green-400 animate-pulse' : 
                  connectionStatus === 'connecting' ? 'bg-yellow-400 animate-pulse' : 
                  'bg-red-400'
                }`} title={`Connection: ${connectionStatus}`}></div>
                <span className={`text-xs ${
                  connectionStatus === 'connected' ? 'text-green-400' : 
                  connectionStatus === 'connecting' ? 'text-yellow-400' : 
                  'text-red-400'
                }`}>
                  {connectionStatus === 'connected' ? 'Live' : 
                   connectionStatus === 'connecting' ? 'Connecting...' : 
                   'Offline'}
                </span>
              </div>
              
              {/* Message count indicator */}
              <div className="text-xs text-gray-400 bg-[#2b2d31] px-2 py-1 rounded" title="Total messages in thread">
                {visibleMessages.length} message{visibleMessages.length !== 1 ? 's' : ''}
              </div>
              
              {/* Last activity indicator */}
              <div className="text-xs text-gray-400" title={`Last activity: ${lastActivity.toLocaleTimeString()}`}>
                {lastActivity.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </div>
              
              {/* Sound toggle */}
              <button
                onClick={() => setSoundEnabled(!soundEnabled)}
                className={`p-1 rounded ${soundEnabled ? 'text-green-400' : 'text-gray-400'}`}
                title={soundEnabled ? 'Disable sound notifications' : 'Enable sound notifications'}
              >
                {soundEnabled ? (
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.617.794L4.383 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.383l4.017-2.794a1 1 0 011.617.794zM12.293 7.293a1 1 0 011.414 0L15 8.586l1.293-1.293a1 1 0 111.414 1.414L16.414 10l1.293 1.293a1 1 0 01-1.414 1.414L15 11.414l-1.293 1.293a1 1 0 01-1.414-1.414L13.586 10l-1.293-1.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.617.794L4.383 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.383l4.017-2.794a1 1 0 011.617.794zM12.293 7.293a1 1 0 011.414 0L15 8.586l1.293-1.293a1 1 0 111.414 1.414L16.414 10l1.293 1.293a1 1 0 01-1.414 1.414L15 11.414l-1.293 1.293a1 1 0 01-1.414-1.414L13.586 10l-1.293-1.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                )}
              </button>
              
              <div className="relative">
                <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-2 top-2.5" />
                <input
                  value={messageQuery}
                  onChange={(e) => setMessageQuery(e.target.value)}
                  placeholder="Search messages"
                  className="w-56 pl-7 pr-2 py-2 rounded bg-[#2b2d31] text-gray-100 text-sm placeholder-gray-400"
                />
              </div>
              <button
                onClick={() => {
                  setShowMembers(true);
                  fetchForumMembers();
                }}
                className="p-1 text-gray-300 hover:text-gray-100 transition-colors"
                title="View Members"
              >
                <UserGroupIcon className="w-5 h-5" />
              </button>
              <BellAlertIcon className="w-5 h-5 text-gray-300" title="Notifications" />
            </div>
          </div>
          {!selectedThread ? (
            <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">Choose a thread from the left to start chatting.</div>
          ) : (
            <>
              <div 
                className="flex-1 overflow-y-auto px-4 py-4 space-y-2"
                data-message-container="true"
                data-total-messages={visibleMessages.length}
                data-selected-thread={selectedThread?.id}
                data-thread-title={selectedThread?.title}
              >
                {/* New message indicator */}
                {newMessageCount > 0 && (
                  <div className="flex items-center justify-center py-2">
                    <button
                      onClick={handleScrollToBottom}
                      className="px-3 py-1 bg-[#5865f2] hover:bg-[#4854d4] text-white text-xs rounded-full flex items-center gap-2"
                    >
                      <span>↓ {newMessageCount} new message{newMessageCount > 1 ? 's' : ''}</span>
                    </button>
                  </div>
                )}
                
                {visibleMessages.map((m, idx) => {
                  const isYou = m.authorId === currentUser?.uid;
                  const displayName = getUserDisplayName(m.authorId);
                  const canDelete = canDeleteMessage(m.authorId);
                  const isThreadMessage = m.id === 'thread';
                  
                  // Debug logging for message rendering order
                  if (process.env.NODE_ENV === 'development' && idx < 3) {
                    console.log(`Rendering message ${idx}:`, {
                      id: m.id,
                      author: m.authorName,
                      time: m.createdAt,
                      timestamp: new Date(m.createdAt).getTime(),
                      isThread: isThreadMessage,
                      order: idx,
                      key: `${m.id || m.authorId}_${m.createdAt}`
                    });
                  }
                  
                  return (
                    <div 
                      key={`${m.id || m.authorId}_${m.createdAt}`}
                      className={`group flex items-start gap-3 px-4 py-3 rounded-lg hover:bg-[#2b2d31] relative mb-4 border transition-all ${
                        isSelectionMode && selectedMessages.has(m.id || '') && canSelectMessage(m.authorId)
                          ? 'border-[#5865f2] bg-[#5865f2]/10'
                          : 'border-transparent hover:border-gray-600'
                      }`}
                      data-message-id={m.id}
                      data-message-time={m.createdAt}
                      data-message-order={idx}
                    >
                      {/* Selection checkbox - Only shown in selection mode */}
                      {isSelectionMode && (
                        <div className="flex-shrink-0 mt-2">
                          {canSelectMessage(m.authorId) ? (
                            <input
                              type="checkbox"
                              checked={selectedMessages.has(m.id || '')}
                              onChange={() => toggleMessageSelection(m.id || '')}
                              className="w-4 h-4 text-[#5865f2] bg-[#1e1f22] border-gray-600 rounded focus:ring-[#5865f2] focus:ring-2 cursor-pointer"
                            />
                          ) : (
                            <div 
                              className="w-4 h-4 bg-[#1e1f22] border border-gray-600 rounded opacity-50 cursor-not-allowed"
                              title="You can only select your own messages"
                            />
                          )}
                        </div>
                      )}
                      
                      {/* Avatar - Always shown for each message */}
                      <UserProfileLink 
                        userId={m.authorId}
                        className="flex-shrink-0"
                      >
                        {getUserProfilePicture(m.authorId) ? (
                          <img 
                            src={getUserProfilePicture(m.authorId)} 
                            alt={getUserDisplayName(m.authorId)} 
                            className="w-10 h-10 rounded-full object-cover flex-shrink-0 border-2 border-[#2b2d31] hover:border-[#5865f2] transition-colors" 
                            onError={(e) => {
                              // Fallback to initials if image fails to load
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                              target.nextElementSibling?.classList.remove('hidden');
                            }}
                          />
                        ) : (
                          <div 
                            className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-semibold flex-shrink-0 border-2 border-[#2b2d31] ${getAvatarColor(m.authorId)}`}
                          >
                            {getInitials(getUserDisplayName(m.authorId))}
                          </div>
                        )}
                      </UserProfileLink>
                      
                      {/* Message content */}
                      <div className="min-w-0 flex-1">
                        {/* Header with name and timestamp */}
                        <div className="flex items-baseline gap-3 mb-2">
                          <div className="font-semibold text-sm text-gray-100">
                            <UserProfileLink 
                              userId={m.authorId}
                              className="hover:text-[#5865f2] transition-colors"
                            />
                            {isYou && <span className="ml-2 text-[10px] text-gray-400 font-normal">(you)</span>}
                          </div>
                          <div className="text-[11px] text-gray-400">
                            {safeTimestampToDate(m.createdAt).toLocaleString()}
                          </div>
                          {canDelete && (
                            <button
                              onClick={() => setShowDeleteConfirm({
                                messageId: isThreadMessage ? 'thread' : m.id || '',
                                authorId: m.authorId,
                                content: m.content
                              })}
                              className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity p-1 text-red-400 hover:text-red-300 hover:bg-red-400/10 rounded"
                              title={isAdmin ? "Delete message (Admin)" : "Delete your message"}
                            >
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H2a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                            </button>
                          )}
                        </div>
                        
                        {/* Message text */}
                        <div className="text-sm text-gray-100 whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: renderTextWithLinks(m.content) }} />
                        
                        {/* Images */}
                        {m.images && m.images.length > 0 && (
                          <div className="mt-3">
                            <div className="flex flex-wrap gap-2">
                              {m.images.slice(0, 4).map((url: string, imageIndex: number) => (
                                <div key={`${m.id || m.authorId}_${m.createdAt}_${imageIndex}`} className="relative group">
                                  <img 
                                    src={url} 
                                    alt={`${displayName} ${safeTimestampToDate(m.createdAt).toLocaleTimeString()}`} 
                                    className="w-20 h-20 rounded-lg object-cover cursor-pointer hover:opacity-90 transition-all duration-200 hover:scale-105 shadow-lg"
                                    onClick={() => openFullScreenImage(url)}
                                    title="Click to view full size"
                                    onError={(e) => {
                                      const target = e.target as HTMLImageElement;
                                      target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAiIGhlaWdodD0iODAiIHZpZXdCb3g9IjAgMCA4MCA4MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjgwIiBoZWlnaHQ9IjgwIiBmaWxsPSIjM0E0OTU5Ii8+CjxwYXRoIGQ9Ik0yMCAyMEg2MFY2MEgyMFYyMFoiIGZpbGw9IiM2Qjc4ODkiLz4KPHBhdGggZD0iTTI2IDI2TDM0IDM0TDU0IDE0IiBzdHJva2U9IndoaXRlIiBzdHJva2Utd2lkdGg9IjMiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPgo8L3N2Zz4K';
                                    }}
                                  />
                                  {/* Image overlay with zoom icon */}
                                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 rounded-lg flex items-center justify-center">
                                    <svg className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                                    </svg>
                                  </div>
                                </div>
                              ))}
                            </div>
                            {/* Show more images indicator */}
                            {m.images.length > 4 && (
                              <div className="mt-2">
                                <button
                                  onClick={() => {
                                    // Show all images in a grid modal
                                    setFullScreenImage(m.images[0]); // This will be enhanced later
                                  }}
                                  className="text-xs text-[#5865f2] hover:text-[#4752c4] transition-colors"
                                >
                                  +{m.images.length - 4} more images
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
                
                {/* Typing indicator */}
                {typingUsers.length > 0 && (
                  <div className="flex items-center gap-2 px-2 py-2 text-gray-400 text-sm">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                    <span>
                      {typingUsers.length === 1 
                        ? `${typingUsers[0].userName} is typing...`
                        : `${typingUsers.length} people are typing...`
                      }
                    </span>
                  </div>
                )}
                
                <div ref={messagesEndRef} />
              </div>

              {/* Message input */}
              <div className="p-2 md:p-4 border-t border-[#1e1f22] mb-16 md:mb-0">
                {/* Selected images preview */}
                {selectedImages.length > 0 && (
                  <div className="mb-3 p-2 bg-[#2b2d31] rounded">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-gray-400">{selectedImages.length} image(s) selected</span>
                      <button
                        onClick={() => setSelectedImages([])}
                        className="text-xs text-red-400 hover:text-red-300"
                      >
                        Clear all
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {selectedImages.map((image, index) => (
                        <div key={index} className="relative">
                          <img
                            src={URL.createObjectURL(image)}
                            alt={`Preview ${index + 1}`}
                            className="w-16 h-16 object-cover rounded"
                          />
                          <button
                            onClick={() => removeSelectedImage(index)}
                            className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600"
                          >
                            <XMarkIcon className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-1 md:gap-2">
                  {/* Image picker */}
                  <div className="relative">
                    <button 
                      className="p-1 md:p-2 rounded bg-[#2b2d31] hover:bg-[#2a2c30] text-gray-300" 
                      title="Attach image"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <PaperClipIcon className="w-4 h-4 md:w-5 md:h-5" />
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleImageSelect}
                      className="hidden"
                    />
                  </div>

                  <input
                    type="text"
                    value={commentDrafts[selectedThread?.id || ''] || ''}
                    onChange={(e) => handleCommentInputChange(selectedThread?.id || '', e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && selectedThread?.id) {
                        addComment(selectedThread.id);
                      }
                    }}
                    placeholder={currentUser ? `Message #${selectedThread?.title || 'thread'}` : 'Sign in to chat'}
                    className="flex-1 px-2 md:px-3 py-2 md:py-3 rounded bg-[#383a40] text-gray-100 text-sm placeholder-gray-400 border border-transparent focus:border-[#5865f2]"
                    disabled={!currentUser}
                  />

                  {/* Emoji picker */}
                  <div className="relative">
                    <button 
                      className="p-1 md:p-2 rounded bg-[#2b2d31] hover:bg-[#2a2c30] text-gray-300" 
                      title="Add emoji"
                      onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    >
                      <FaceSmileIcon className="w-4 h-4 md:w-5 md:h-5" />
                    </button>
                    
                    {/* Emoji picker dropdown */}
                    {showEmojiPicker && (
                      <div 
                        ref={emojiPickerRef}
                        className="absolute bottom-full right-0 mb-2 bg-[#2b2d31] border border-[#1e1f22] rounded-lg p-3 max-h-80 w-80 md:w-96 overflow-y-auto z-50"
                      >
                        <div className="grid grid-cols-6 md:grid-cols-8 gap-2">
                          {emojis.map((emoji, index) => (
                            <button
                              key={index}
                              onClick={() => addEmoji(emoji)}
                              className="w-10 h-10 md:w-8 md:h-8 text-lg md:text-base hover:bg-[#404249] rounded flex items-center justify-center transition-colors"
                            >
                              {emoji}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <button
                    onClick={() => selectedThread?.id && addComment(selectedThread.id)}
                    disabled={!currentUser || ((commentDrafts[selectedThread?.id || ''] || '').trim() === '' && selectedImages.length === 0)}
                    className="px-2 md:px-4 py-2 md:py-3 bg-[#5865f2] hover:bg-[#4854d4] text-white rounded text-sm disabled:opacity-50"
                  >
                    {uploadingImages ? 'Uploading...' : 'Send'}
                  </button>
                </div>
                {!currentUser && (
                  <div className="text-[11px] text-gray-400 mt-1">Sign in to comment.</div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Info panel */}
        <div className="w-80 bg-[#2b2d31] text-gray-200 border-l border-[#1e1f22] hidden lg:flex flex-col">
          <div className="px-4 py-3 border-b border-[#1e1f22] text-sm font-semibold">Thread Info</div>
          {!selectedThread ? (
            <div className="p-4 text-sm text-gray-400">No thread selected.</div>
          ) : (
            <div className="p-4 space-y-3 text-sm">
              <div>
                <div className="text-gray-400 text-[11px] uppercase mb-1">Title</div>
                <div className="text-gray-100 font-medium break-words">{selectedThread?.title}</div>
              </div>
              <div>
                <div className="text-gray-400 text-[11px] uppercase mb-1">Topic</div>
                <div className="text-gray-100">{selectedThread?.topic}</div>
              </div>
              <div>
                <div className="text-gray-400 text-[11px] uppercase mb-1">Created By</div>
                <div className="text-gray-100">
                  {selectedThread?.createdBy ? (
                    <UserProfileLink 
                      userId={selectedThread.createdBy}
                      className="hover:text-[#5865f2] transition-colors"
                    />
                  ) : (
                    selectedThread?.createdByName || 'Unknown'
                  )}
                </div>
              </div>
              <div>
                <div className="text-gray-400 text-[11px] uppercase mb-1">Created</div>
                <div className="text-gray-100">{selectedThread?.createdAt ? new Date(selectedThread.createdAt).toLocaleString() : 'Unknown'}</div>
              </div>
              <div>
                <div className="text-gray-400 text-[11px] uppercase mb-1">Messages</div>
                <div className="text-gray-100">{(selectedThread?.comments || []).length + 1}</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mobile overlays */}
      {showServersMobile && (
        <div className="md:hidden fixed inset-0 z-[70] bg-black/50" onClick={() => setShowServersMobile(false)}>
          <div className="absolute left-0 top-0 h-full w-20 bg-[#1e1f22] p-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex flex-col items-center space-y-3">
              {topics.map((t) => {
                const isActive = selectedTopic === t;
                const label = t === 'all' ? 'A' : t.charAt(0).toUpperCase();
                return (
                  <button
                    key={t}
                    onClick={() => {
                      setSelectedTopic(t);
                      setShowServersMobile(false);
                    }}
                    className={`w-12 h-12 rounded-full flex items-center justify-center text-sm font-semibold transition-transform ${isActive ? 'bg-[#5865f2] text-white scale-105' : 'bg-[#313338] text-gray-300 hover:scale-105'}`}
                    title={t}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {showChannelsMobile && (
        <div className="md:hidden fixed inset-0 z-[70] bg-black/50" onClick={() => setShowChannelsMobile(false)}>
          <div className="absolute left-0 top-0 h-full w-[85%] max-w-sm bg-[#2b2d31] text-gray-200 flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-3 py-3 border-b border-[#1e1f22] flex items-center justify-between">
              <div className="text-sm font-semibold truncate">{selectedTopic === 'all' ? 'All Topics' : selectedTopic}</div>
              <button
                onClick={() => setIsCreatingThread((v) => !v)}
                className="text-xs bg-[#5865f2] hover:bg-[#4854d4] text-white px-2 py-1 rounded"
                disabled={!currentUser}
              >
                New
              </button>
            </div>
            <div className="px-3 py-2">
              <div className="relative">
                <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-2 top-2.5" />
                <input
                  value={threadFilter}
                  onChange={(e) => setThreadFilter(e.target.value)}
                  placeholder="Search threads"
                  className="w-full pl-7 pr-2 py-2 rounded bg-[#1e1f22] text-gray-100 text-sm placeholder-gray-400"
                />
              </div>
            </div>
            {isCreatingThread && (
              <div className="p-3 border-b border-[#1e1f22] space-y-2">
                <input
                  type="text"
                  placeholder="Thread title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full px-2 py-2 rounded bg-[#1e1f22] text-gray-100 text-sm placeholder-gray-400"
                />
                <textarea
                  placeholder="What do you want to discuss?"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  className="w-full px-2 py-2 rounded bg-[#1e1f22] text-gray-100 text-sm min-h-[70px] placeholder-gray-400"
                />
                <div className="flex items-center gap-2">
                  {/* Second topic select - mobile */}
                  <CustomDropdown
                    value={topic}
                    onChange={setTopic}
                    options={['General', 'ICAG', 'CITG', 'Accounting', 'Taxation', 'Audit', 'Exam Prep'].map((t) => ({
                      value: t,
                      label: t
                    }))}
                    placeholder="Select Topic"
                    className="flex-1 px-2 py-2 rounded bg-[#1e1f22] text-gray-100 text-sm"
                  />
                  <button
                    onClick={createThread}
                    disabled={!currentUser || !title.trim() || !content.trim()}
                    className="px-3 py-2 bg-[#5865f2] hover:bg-[#4854d4] text-white rounded text-sm disabled:opacity-50"
                  >
                    Post
                  </button>
                </div>
                {!currentUser && (
                  <div className="text-[11px] text-gray-400">Sign in to create a thread.</div>
                )}
              </div>
            )}
            <div className="px-3 py-2 text-[11px] uppercase tracking-wide text-gray-400">Threads</div>
            <div className="flex-1 overflow-y-auto">
              {loading && <div className="px-3 py-2 text-gray-400 text-sm">Loading...</div>}
              {filteredBySearch.map((th) => {
                const isActive = selectedThreadId === th.id;
                const messagesCount = (th.comments?.length || 0) + 1;
                return (
                  <button
                    key={th.id}
                    onClick={() => {
                      setSelectedThreadId(th.id || null);
                      setShowChannelsMobile(false);
                    }}
                    className={`w-full text-left px-3 py-2 hover:bg-[#404249] ${isActive ? 'bg-[#404249]' : ''}`}
                  >
                    <div className="flex items-center gap-3">
                      {/* Thread Profile Picture - Only show when exists */}
                      {th.profilePicture && (
                        <div className="flex-shrink-0">
                          <img 
                            src={th.profilePicture} 
                            alt="Thread profile" 
                            className="w-10 h-10 rounded-full object-cover border border-[#1e1f22]"
                          />
                        </div>
                      )}
                      
                      {/* Thread Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-sm text-gray-100 truncate"># {th.title}</div>
                          <span className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1 rounded-full bg-[#1e1f22] text-gray-300 text-[11px]">{messagesCount}</span>
                        </div>
                        <div className="text-[11px] text-gray-400 truncate">
                          {th.createdBy ? (
                            <UserProfileLink 
                              userId={th.createdBy}
                              className="hover:text-[#5865f2] transition-colors"
                            />
                          ) : (
                            th.createdByName
                          )} • {new Date(th.updatedAt || th.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
              {filteredBySearch.length === 0 && (
                <div className="px-3 py-2 text-gray-400 text-sm">No threads yet.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Mobile chat area improvements */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-[#313338] border-t border-[#1e1f22] p-2 z-50">
        <div className="flex items-center gap-2">
          <button 
            onClick={() => setShowServersMobile(true)}
            className="p-2 rounded bg-[#2b2d31] text-gray-200"
          >
            <Squares2X2Icon className="w-5 h-5" />
          </button>
          <button 
            onClick={() => setShowChannelsMobile(true)}
            className="p-2 rounded bg-[#2b2d31] text-gray-200"
          >
            <Bars3Icon className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <div className="text-xs text-gray-400 truncate">
              {selectedThread ? `# ${selectedThread.title}` : 'Select a thread'}
            </div>
            <div className="text-xs text-gray-300">
              {connectionStatus === 'connected' ? '🟢 Live' : '🔴 Offline'}
            </div>
          </div>
        </div>
      </div>

      {/* AI Chat Button */}
      <AIChatButton />

      {/* Full-screen image viewer */}
      {fullScreenImage && (
        <div className="fixed inset-0 z-55 flex items-center justify-center bg-black/90">
          <div className="relative max-w-full max-h-full">
            <img
              src={fullScreenImage}
              alt="Full size"
              className="max-w-full max-h-full object-contain"
            />
            
            {/* Navigation arrows for multiple images */}
            {fullScreenImage && fullScreenImage.includes('multiple_images') && (
              <>
                <button
                  onClick={() => {/* Previous image logic */}}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
                  title="Previous image"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={() => {/* Next image logic */}}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
                  title="Next image"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </>
            )}
            
            {/* Close button */}
            <button
              onClick={closeFullScreenImage}
              className="absolute top-4 right-4 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
              title="Close"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
            
            {/* Image counter */}
            <div className="absolute bottom-4 left-4 px-3 py-1 bg-black/50 text-white text-sm rounded-full">
              Image 1 of 1
            </div>
          </div>
        </div>
      )}

      {/* Edit thread modal */}
      {showEditThread && selectedThread && (
        <div className="fixed inset-0 z-60 flex items-center justify-center bg-black/50">
          <div className="bg-[#2b2d31] rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-100">Edit Thread</h3>
              <button
                onClick={() => setShowEditThread(false)}
                className="p-2 text-gray-400 hover:text-gray-200 rounded transition-colors"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-200 mb-2">Thread Title</label>
                <input
                  type="text"
                  value={editingTitle}
                  onChange={(e) => setEditingTitle(e.target.value)}
                  className="w-full px-3 py-2 rounded bg-[#1e1f22] text-gray-100 text-sm placeholder-gray-400 border border-[#404249] focus:border-[#5865f2] focus:outline-none"
                  placeholder="Enter thread title"
                />
              </div>

              {/* Content */}
              <div>
                <label className="block text-sm font-medium text-gray-200 mb-2">Content</label>
                <textarea
                  value={editingContent}
                  onChange={(e) => setEditingContent(e.target.value)}
                  className="w-full px-3 py-2 rounded bg-[#1e1f22] text-gray-100 text-sm placeholder-gray-400 border border-[#404249] focus:border-[#5865f2] focus:outline-none min-h-[100px] resize-y"
                  placeholder="What do you want to discuss?"
                />
              </div>

              {/* Topic */}
              <div>
                <label className="block text-sm font-medium text-gray-200 mb-2">Topic</label>
                <CustomDropdown
                  value={editingTopic}
                  onChange={setEditingTopic}
                  options={['General', 'ICAG', 'CITG', 'Accounting', 'Taxation', 'Audit', 'Exam Prep'].map((t) => ({
                    value: t,
                    label: t
                  }))}
                  placeholder="Select Topic"
                  className="w-full px-3 py-2 rounded bg-[#1e1f22] text-gray-100 text-sm border border-[#404249] focus:border-[#5865f2] focus:outline-none"
                />
              </div>

              {/* Profile Picture Upload */}
              <div className="p-3 bg-[#2b2d31] rounded">
                <div className="flex items-center gap-3 mb-2">
                  <div className="flex-shrink-0">
                    {editingProfilePicture ? (
                      <img 
                        src={URL.createObjectURL(editingProfilePicture)} 
                        alt="New profile" 
                        className="w-12 h-12 rounded-full object-cover border border-[#1e1f22]"
                      />
                    ) : selectedThread.profilePicture ? (
                      <img 
                        src={selectedThread.profilePicture} 
                        alt="Current profile" 
                        className="w-12 h-12 rounded-full object-cover border border-[#1e1f22]"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-[#404249] flex items-center justify-center border-2 border-dashed border-[#5865f2]">
                        <svg className="w-6 h-6 text-[#5865f2]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-100">Profile Picture</div>
                    <div className="text-xs text-gray-400">
                      {editingProfilePicture ? 'New image selected' : selectedThread.profilePicture ? 'Current image' : 'No profile picture'}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleEditProfilePictureSelect}
                      className="hidden"
                      id="edit-profile-picture"
                    />
                    <label
                      htmlFor="edit-profile-picture"
                      className="px-3 py-1 bg-[#5865f2] hover:bg-[#4752c4] text-white text-xs rounded transition-colors cursor-pointer"
                    >
                      {editingProfilePicture ? 'Change' : 'Upload'}
                    </label>
                    {editingProfilePicture && (
                      <button
                        onClick={removeEditProfilePicture}
                        className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded transition-colors"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Thumbnail Upload */}
              <div className="p-3 bg-[#2b2d31] rounded">
                <div className="flex items-center gap-3 mb-2">
                  <div className="flex-shrink-0">
                    {editingThumbnail ? (
                      <img 
                        src={URL.createObjectURL(editingThumbnail)} 
                        alt="New thumbnail" 
                        className="w-16 h-12 rounded object-cover border border-[#1e1f22]"
                      />
                    ) : selectedThread.thumbnail ? (
                      <img 
                        src={selectedThread.thumbnail} 
                        alt="Current thumbnail" 
                        className="w-16 h-12 rounded object-cover border border-[#1e1f22]"
                      />
                    ) : (
                      <div className="w-16 h-12 rounded bg-[#404249] flex items-center justify-center border-2 border-dashed border-[#5865f2]">
                        <svg className="w-6 h-6 text-[#5865f2]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 002 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-100">Thumbnail</div>
                    <div className="text-xs text-gray-400">
                      {editingThumbnail ? 'New image selected' : selectedThread.thumbnail ? 'Current image' : 'No thumbnail'}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleEditThumbnailSelect}
                      className="hidden"
                      id="edit-thumbnail"
                    />
                    <label
                      htmlFor="edit-thumbnail"
                      className="px-3 py-1 bg-[#5865f2] hover:bg-[#4752c4] text-white text-xs rounded transition-colors cursor-pointer"
                    >
                      {editingThumbnail ? 'Change' : 'Upload'}
                    </label>
                    {editingThumbnail && (
                      <button
                        onClick={removeEditThumbnail}
                        className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded transition-colors"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowEditThread(false)}
                className="flex-1 px-4 py-2 bg-[#404249] hover:bg-[#4f545c] text-gray-200 rounded transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={updateThread}
                disabled={!editingTitle.trim() || !editingContent.trim() || uploadingEditAssets}
                className="flex-1 px-4 py-2 bg-[#5865f2] hover:bg-[#4854d4] text-white rounded transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {uploadingEditAssets ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Updating...
                  </>
                ) : (
                  'Update Thread'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-60 flex items-center justify-center bg-black/50">
          <div className="bg-[#2b2d31] rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-100">Delete Message</h3>
                <p className="text-sm text-gray-400">
                  {isAdmin ? "Are you sure you want to delete this message?" : "Are you sure you want to delete your message?"}
                </p>
              </div>
            </div>
            
            <div className="mb-4 p-3 bg-[#1e1f22] rounded text-sm text-gray-300 max-h-32 overflow-y-auto">
              {showDeleteConfirm?.content && showDeleteConfirm.content.length > 200 
                ? `${showDeleteConfirm.content.substring(0, 200)}...` 
                : showDeleteConfirm?.content
              }
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="flex-1 px-4 py-2 bg-[#404249] hover:bg-[#4f545c] text-gray-200 rounded transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteMessage(
                  showDeleteConfirm?.messageId || '', 
                  showDeleteConfirm?.messageId === 'thread'
                )}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk delete confirmation modal */}
      {showBulkDeleteConfirm && (
        <div className="fixed inset-0 z-60 flex items-center justify-center bg-black/50">
          <div className="bg-[#2b2d31] rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-100">Delete Selected Messages</h3>
                <p className="text-sm text-gray-400">
                  Are you sure you want to delete {selectedMessages.size} selected message{selectedMessages.size !== 1 ? 's' : ''}? This action cannot be undone.
                </p>
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowBulkDeleteConfirm(false)}
                className="flex-1 px-4 py-2 bg-[#404249] hover:bg-[#4f545c] text-gray-200 rounded transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={deleteSelectedMessages}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
              >
                Delete {selectedMessages.size} Message{selectedMessages.size !== 1 ? 's' : ''}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Members modal */}
      {showMembers && (
        <div className="fixed inset-0 z-60 flex items-center justify-center bg-black/50">
          <div className="bg-[#2b2d31] rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-100">Forum Members</h3>
              <button
                onClick={() => setShowMembers(false)}
                className="p-2 text-gray-400 hover:text-gray-200 rounded transition-colors"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            
            {loadingMembers ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-6 h-6 border-2 border-[#5865f2] border-t-transparent rounded-full animate-spin"></div>
                <span className="ml-2 text-gray-400">Loading members...</span>
              </div>
            ) : (
              <div className="space-y-3">
                {forumMembers.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    <UserGroupIcon className="w-12 h-12 mx-auto mb-3 text-gray-500" />
                    <p>No members found</p>
                  </div>
                ) : (
                  forumMembers.map((member) => (
                    <div key={member.uid} className="flex items-center gap-3 p-3 bg-[#1e1f22] rounded-lg">
                      {/* Member Avatar */}
                      <div className="flex-shrink-0">
                        {member.avatar ? (
                          <img 
                            src={member.avatar} 
                            alt={member.name} 
                            className="w-10 h-10 rounded-full object-cover border border-[#404249]"
                          />
                        ) : (
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-semibold ${getAvatarColor(member.uid)}`}>
                            {getInitials(member.name)}
                          </div>
                        )}
                      </div>
                      
                      {/* Member Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-100 truncate">{member.name}</span>
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            member.role === 'Creator' 
                              ? 'bg-[#5865f2] text-white' 
                              : 'bg-[#404249] text-gray-300'
                          }`}>
                            {member.role}
                          </span>
                        </div>
                        <div className="text-xs text-gray-400">
                          Last seen: {member.lastSeen?.toLocaleDateString() || 'Unknown'}
                        </div>
                      </div>
                      
                      {/* Online Status */}
                      <div className="flex-shrink-0">
                        <div className="w-3 h-3 rounded-full bg-green-400 animate-pulse" title="Online"></div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
            
            <div className="mt-6 pt-4 border-t border-[#404249]">
              <div className="text-sm text-gray-400 text-center">
                Total Members: {forumMembers.length}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudyForums;


